import { keepPreviousData } from "@tanstack/react-query";

import { useGetControlHealthDashboard } from "api/generated/control-health/control-health";
import {
    CategoryAndTag,
    GetControlHealthDashboardParams,
} from "api/generated/model";
import { DateRange } from "components/filters/dateRange";
import { FILTER_VALUE_DATE_FORMAT } from "components/table/FilterBarDateTimeRange/FilterBarDateTimeRangeUtils";
import {
    TagsQuerySelect,
    useTagsSuspenseQuery,
} from "org/tags/queries/useTagsQuery";

import { buildDefaultDueDateRange } from "../defaultDueDateRange";
import { useControlHealthFilters } from "../filters/ControlHealthFiltersProvider";
import { ControlHealthFilters } from "../filters/types";
import { overdueRangeHours, OverdueRanges } from "../overdueColumns";

export function useControlHealthDashboardQuery(overdueRanges: OverdueRanges) {
    const { filters, dateRange } = useControlHealthFilters();
    const { data: tagCatalogue } = useTagsSuspenseQuery();

    return useGetControlHealthDashboard(
        buildParams(filters, dateRange, tagCatalogue, overdueRanges),
        {
            query: {
                select: (response) => response.data,
                enabled: dateRange !== null,
                placeholderData: keepPreviousData,
                throwOnError: true,
            },
        }
    );
}

function buildParams(
    filters: ControlHealthFilters,
    dateRange: DateRange | null,
    tagCatalogue: TagsQuerySelect,
    overdueRanges: OverdueRanges
): GetControlHealthDashboardParams {
    const tagIds = toTagIds(filters.tags, tagCatalogue);
    // The bounds are required, so an incomplete custom range falls back to the
    // default window. The query is disabled then, so it never reaches the wire.
    const dueDates = dateRange
        ? {
              due_date_start: dateRange.start.format(FILTER_VALUE_DATE_FORMAT),
              due_date_end: dateRange.end.format(FILTER_VALUE_DATE_FORMAT),
          }
        : buildDefaultDueDateRange();

    return {
        ...dueDates,
        table_overdue_under_hours: overdueRangeHours(overdueRanges.under),
        table_overdue_at_least_hours: overdueRangeHours(overdueRanges.atLeast),
        archived_status: filters.showArchivedActivities ? "All" : "NotArchived",
        control_program_status: filters.showInactivePrograms
            ? "All"
            : "Running",
        tag_ids: tagIds.length > 0 ? tagIds : undefined,
        control_program_ids:
            filters.controlPrograms.length > 0
                ? filters.controlPrograms
                : undefined,
        entity_ids: filters.entities.length > 0 ? filters.entities : undefined,
    };
}

function toTagIds(
    selected: CategoryAndTag[],
    { tags, tagCategories }: TagsQuerySelect
): string[] {
    const categoryIdByName = new Map(
        tagCategories.map((category) => [category.name, category.id])
    );

    return selected.flatMap(({ categoryName, tagName }) => {
        const categoryId = categoryIdByName.get(categoryName);
        const tag = tags.find(
            (candidate) =>
                candidate.name === tagName &&
                candidate.tagCategoryId === categoryId
        );
        return tag === undefined ? [] : [tag.id];
    });
}
